import {
  Box,
  type BoxProps,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  type MenuProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import { LuHelpCircle, LuLogOut, LuUser, LuX } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { Link } from '@/components';
import {
  CircularIconContainer,
  Icon,
  IconHamburger,
  IconLeftTailedArrow,
  IconXClose,
} from '@/components/Icons';
import { NavBarLanguageMenu } from '@/features/common';
import {
  INTERVIEW_NAV_BAR_HEIGHT,
  useCandidateSearchParams,
  useGetCandidateQuery,
} from '@/features/interview';
import { getFullname } from '@/utils/identity';
import { variantPath } from '@/utils/variant/variantPath';

import { ACCOUNT_PATH } from '../account/constants';

const MotionBox = motion(Box);

const SUPPORT_EMAIL = 'support@synapsai.ai';

const HelpModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation(['components']);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent mx={4} borderRadius="16px" p={6} position="relative">
        <IconButton
          aria-label="Close"
          icon={<Icon icon={LuX} fontSize="20px" />}
          position="absolute"
          top={4}
          right={4}
          variant="ghost"
          size="sm"
          onClick={onClose}
          color="gray.500"
        />

        <ModalBody p={0}>
          <VStack spacing={6} align="center" textAlign="center">
            <CircularIconContainer icon={LuHelpCircle} />

            <VStack spacing={3}>
              <Text fontSize="xl" fontWeight="bold" color="gray.900">
                {t('components:help.modal.title')}
              </Text>
              <Text fontSize="sm" color="gray.600" maxW="sm">
                <Trans
                  i18nKey="help.modal.description"
                  ns="components"
                  values={{
                    email: SUPPORT_EMAIL,
                  }}
                  components={{
                    strong: (
                      <Link
                        to={`mailto:${SUPPORT_EMAIL}`}
                        fontWeight="semibold"
                        color="brand.600"
                      />
                    ),
                  }}
                />
              </Text>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const SurveyNavBarAccountMenu = ({ ...rest }: Omit<MenuProps, 'children'>) => {
  const { candidateId, token } = useCandidateSearchParams();

  const candidate = useGetCandidateQuery(
    {
      candidateId: candidateId!,
      token: token!,
    },
    {
      enabled: !!candidateId && !!token,
    }
  );

  const fullName = getFullname(candidate.data?.name, candidate.data?.surname);

  if (!candidate.data) return null;

  return (
    <Box color="codGray.900">
      <Menu placement="bottom-end" {...rest}>
        <MenuButton
          bgColor="transparent"
          color="codGray.800"
          borderColor="gray.100"
          borderRadius="8px"
          border="1px solid"
          leftIcon={<Icon icon={LuUser} fontSize="lg" />}
          h="12"
          px="2"
          minW="24"
          isLoading={candidate.isLoading}
          as={Button}
          pointerEvents="none"
        >
          {fullName}
        </MenuButton>
      </Menu>
    </Box>
  );
};

export const SurveyNavBar = (props: BoxProps) => {
  const { isOpen, onToggle } = useDisclosure();
  const {
    isOpen: isHelpModalOpen,
    onOpen: onHelpModalOpen,
    onClose: onHelpModalClose,
  } = useDisclosure();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'components']);

  const SURVEY_MENU_OPTIONS = [
    {
      title: t('components:help.title'),
      icon: LuHelpCircle,
      action: () => {
        onToggle();
        onHelpModalOpen();
      },
    },
    {
      title: t('auth:admin.layout.accountMenu.logout'),
      icon: LuLogOut,
      action: () => navigate(`/logout?redirect=${ACCOUNT_PATH}/login`),
    },
  ] as const;

  return (
    <>
      <Box {...props}>
        <Flex
          zIndex="sticky"
          position="fixed"
          top="0"
          insetStart="0"
          insetEnd="0"
          color="gray.50"
          align="center"
          justify="center"
          pt="safe-top"
          px="4"
          h={INTERVIEW_NAV_BAR_HEIGHT}
          bg="white"
          boxShadow={{ lg: 'layout' }}
          borderBottom={{
            base: '2px solid',
            lg: '1px solid',
          }}
          borderBottomColor={{
            base: 'gray.50',
            lg: 'transparent',
          }}
        >
          <Flex
            grow={1}
            align="center"
            justify="space-between"
            maxW="container.xl"
          >
            {!isOpen && (
              <MotionBox
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                fontSize="18px"
                color="gray.900"
                display={{ base: 'block', lg: 'none' }}
                onClick={() => navigate(-1)}
              >
                <IconLeftTailedArrow />
              </MotionBox>
            )}

            <Box mx={0}>{variantPath('Logo')}</Box>

            <HStack gap={4} display={{ base: 'none', lg: 'flex' }}>
              <Button
                variant="link"
                mr={3}
                color="gray.600"
                leftIcon={<Icon icon={LuHelpCircle} fontSize="24px" />}
                onClick={onHelpModalOpen}
              >
                {t('components:help.title')}
              </Button>
              <SurveyNavBarAccountMenu />
              <NavBarLanguageMenu />
            </HStack>

            <Center
              display={{ base: 'flex', lg: 'none' }}
              w={10}
              h={10}
              borderRadius="full"
              border="1px solid"
              borderColor="brand.50"
              bg="brand.25"
              onClick={onToggle}
              cursor="pointer"
            >
              <AnimatePresence mode="wait" initial={false}>
                {isOpen ? (
                  <MotionBox
                    key="IconX"
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: 90 }}
                    transition={{ duration: 0.15 }}
                    fontSize="24px"
                  >
                    <IconXClose />
                  </MotionBox>
                ) : (
                  <MotionBox
                    key="IconHamburger"
                    initial={{ opacity: 0, rotate: 90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: -90 }}
                    transition={{ duration: 0.15 }}
                    fontSize="24px"
                  >
                    <IconHamburger />
                  </MotionBox>
                )}
              </AnimatePresence>
            </Center>
          </Flex>
        </Flex>
        <Box h={INTERVIEW_NAV_BAR_HEIGHT} />

        {isOpen && (
          <MotionBox
            position="absolute"
            top={INTERVIEW_NAV_BAR_HEIGHT}
            left="0"
            width="100%"
            bg="white"
            zIndex="100"
            initial={{ height: 0 }}
            animate={{ height: '150px' }}
            exit={{ height: 0 }}
            overflow="hidden"
            borderBottom="1px solid"
            borderBottomColor="gray.50"
          >
            <VStack spacing="4" padding="4">
              {SURVEY_MENU_OPTIONS.map((item) => (
                <HStack
                  key={item.title}
                  w="full"
                  gap={3}
                  cursor="pointer"
                  p={2}
                  border="1px solid"
                  borderColor="gray.50"
                  transition="linear 0.1s"
                  borderRadius={8}
                  color="gray.900"
                  fontWeight="medium"
                  onClick={item.action}
                  _hover={{
                    borderColor: 'brand.100',
                    bg: 'brand.25',
                  }}
                >
                  <Center
                    w="2.25rem"
                    h="2.25rem"
                    bg="brand.50"
                    borderRadius={8}
                  >
                    <Icon icon={item.icon} fontSize="24px" color="brand.500" />
                  </Center>

                  <Text>{item.title}</Text>
                </HStack>
              ))}

              <Flex w="full">
                <NavBarLanguageMenu renderAsBottomSheet />
              </Flex>
            </VStack>
          </MotionBox>
        )}
      </Box>

      <AnimatePresence>
        {isOpen && (
          <MotionBox
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(0, 0, 0, 0.6)"
            zIndex="50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onToggle}
          />
        )}
      </AnimatePresence>

      <HelpModal isOpen={isHelpModalOpen} onClose={onHelpModalClose} />
    </>
  );
};
